import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import GISNavbar from '../GISNavbar';
import Button from 'src/components/controls/forms/redesign/FormButton';
import GISPortalFormDetails from './GISPortalFormDetails';
import CardContainer from 'src/components/common/CardContainer.redesign';
import GISClientCard from './GISClientCard';
import Client from 'src/models/Client';
import GISProjectCard from './GISProjectCard';
import Project from 'src/models/Project';
import useFetch from 'use-http';
import PagedResultSet from 'src/models/common/PagedResultSet';
import Filter from 'src/components/common/Filter';
import { ODataOptions } from 'src/util/ODataBuilder';
import actions from 'src/actions';
import { IRootState } from 'src/reducers';
import { connect } from 'react-redux';
import selectors from 'src/selectors';
import GISMapData from 'src/models/GISMapData';
import GISToast from './components/GISPortalToast';
import Form from 'src/components/controls/forms/Form';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import GISPortalLayerSettings from './GISPortalLayerSettings';
import {
    IMapConfig,
    TRACT_LAYERS,
    isTractLayerEnabled,
    parseMapConfig,
    withTractLayersEnabled,
} from 'src/components/project/sections/GISComponents/MapConfig';

interface IGISPortalDispatchProps {
    initializePage: () => void;
    getClients: (options: ODataOptions, resetSearch: boolean) => void;
    incrementPage: () => void;
    decrementPage: () => void;
}

interface IGISPortalStoreProps {
    clients: PagedResultSet<Client>;
}

type IGISPortalPageProps = IGISPortalDispatchProps & IGISPortalStoreProps;

export const GISPortalPage: React.FC<IGISPortalPageProps> = (props) => {
    const { handleSubmit, register, control, errors, reset, setValue } = useForm();
    const { get, put, post, response } = useFetch();

    const [projects, setProjects] = useState<PagedResultSet<Project>>(undefined);
    const [selectedClient, setSelectedClient] = useState<Client>(null);
    const [projectMapExists, setProjectMapExists] = useState<boolean>(false);
    const [selectedProject, setSelectedProject] = useState<Project>(undefined);
    const [isOnLastPage, setIsOnLastPage] = useState<boolean>(false);
    const [isProjectsLoading, setIsProjectsLoading] = useState<boolean>(false);
    const [isClientsLoading, setIsClientsLoading] = useState<boolean>(false);
    const [mapData, setMapData] = useState<GISMapData>(undefined);
    const [form, setForm] = useState<FormData>(new FormData());

    const [configFileName, setConfigFileName] = useState<string>('');
    const [tractsFileName, setTractsFileName] = useState<string>('');
    const [aoiFileName, setAoiFileName] = useState<string>('');

    const [toast, setToast] = useState<{ message: string; color?: string; persistent?: boolean } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [canNavigateToMapping, setCanNavigateToMapping] = useState<boolean>(false);

    // Map Layers: the project's config.json as loaded (null when it has none), the checkboxes, and
    // whether they changed since. "Unreadable" means a config exists that couldn't be read or parsed;
    // saving the checkboxes then would replace it with one that has lost its colors and aliases, so
    // they stay locked.
    const [mapConfig, setMapConfig] = useState<IMapConfig | null>(null);
    const [layerEnabled, setLayerEnabled] = useState<Record<string, boolean>>({});
    const [layersChanged, setLayersChanged] = useState<boolean>(false);
    const [isConfigUnreadable, setIsConfigUnreadable] = useState<boolean>(false);

    // The client or project selection that is current: every click on one bumps it. A load or save
    // that awaits checks it before touching state, so a slow response for a selection someone has
    // since left can't fill in the one they're on. Otherwise project A's config could land in project
    // B's checkboxes, and a Save would write A's layer settings (or file ids) into B.
    const selectionRef = useRef(0);

    const loadLayerSettings = (config: IMapConfig | null) => {
        setMapConfig(config);
        setLayerEnabled(TRACT_LAYERS.reduce<Record<string, boolean>>((all, { key }) => ({ ...all, [key]: isTractLayerEnabled(config, key) }), {}));
        setLayersChanged(false);
    };

    const setLayer = (key: string, enabled: boolean) => {
        setLayerEnabled((current) => ({ ...current, [key]: enabled }));
        setLayersChanged(true);
    };

    useEffect(() => {
        if (props.clients) {
            setIsOnLastPage(props.clients.pageNumber * props.clients.pageSize < props.clients.totalRecords);
        }
    }, [props.clients]);

    useEffect(() => {
        setIsClientsLoading(true);
        props.getClients(new ODataOptions(), true);
        setIsClientsLoading(false);
    }, []);

    const resetForm = () => {
        reset();
        setMapData(undefined);
        setConfigFileName('');
        setTractsFileName('');
        setAoiFileName('');
        setCanNavigateToMapping(false);
        loadLayerSettings(null);
        setIsConfigUnreadable(false);
    };

    const getClient = async (client: Client) => {
        selectionRef.current += 1;
        // Whatever was loading or saving was for the last selection, which now changes nothing.
        setIsLoading(false);
        setToast(null);
        setSelectedClient(client);
        resetForm();
        setSelectedProject(undefined);
        getProjects(client.clientNumber);
    };

    const getProjects = async (clientNumber: number) => {
        const selection = selectionRef.current;
        setProjects(undefined);
        setIsProjectsLoading(true);
        const result = await get(`/v1.0/clients/${clientNumber}/Projects`);
        if (selection !== selectionRef.current) {
            return;
        }
        if (response.ok) {
            setProjects(result);
            setIsProjectsLoading(false);
        } else {
            console.log('bad request', response.data);
        }
    };

    const displayExistingClients = () => {
        if (isClientsLoading) {
            return <div>Loading clients...</div>;
        }

        if (!props.clients) {
            return <div>No clients</div>;
        }

        return props.clients?.items?.map((c: Client) => {
            return <GISClientCard key={`client-${c.id}`} client={c} selectedClientId={selectedClient?.id} onClick={() => getClient(c)} />;
        });
    };

    const displayClientProjects = () => {
        if (isProjectsLoading) {
            return <div>Loading projects...</div>;
        }

        if (!selectedClient || !projects) {
            return <div>No projects</div>;
        }

        if (projects?.items.length < 0) {
            return <div>No projects set up for {selectedClient ? selectedClient.cloakName : 'this client'}</div>;
        }

        const filteredProjects = projects?.items.filter((p: Project) => !p.updateNeeded);

        if (filteredProjects.length === 0) {
            return <div>One or more projects still need to be set up for {selectedClient ? selectedClient.cloakName : 'this client'}</div>;
        }

        return filteredProjects.map((p: Project) => {
            return <GISProjectCard key={`project-${p.id}`} project={p} onClick={() => getMapData(p)} />;
        });
    };

    const handleClientFilterChanged = (options: ODataOptions) => {
        props.getClients(options, true);
    };

    const getMapData = async (project: Project) => {
        selectionRef.current += 1;
        const selection = selectionRef.current;
        const isStale = () => selection !== selectionRef.current;
        resetForm();
        setForm(new FormData());
        setSelectedProject(project);
        setProjectMapExists(false);
        showToast('Loading data ', `rgba(78,117,154,0.5)`, true);
        setIsLoading(true);

        const result = await get(`v1.0/gis/clients/${selectedClient.id}/projects/${project.id}/projectMap`);
        if (isStale()) {
            return;
        }

        if (response.ok) {
            if (result) {
                if (result.configS3Id) {
                    const configBody = await get(`/v1.0/s3Bucket/by-id/${result.configS3Id}`);
                    if (isStale()) {
                        return;
                    }
                    if (response.ok) {
                        const contentDisposition = response.headers.get('Content-Disposition');
                        let downloadFileName = '';

                        if (contentDisposition) {
                            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                            if (fileNameMatch && fileNameMatch[1]) {
                                downloadFileName = fileNameMatch[1];
                            }
                        }
                        setConfigFileName(downloadFileName);

                        const config = parseMapConfig(configBody);
                        if (config) {
                            loadLayerSettings(config);
                        } else {
                            setIsConfigUnreadable(true);
                        }
                    } else {
                        showToast('Failed to fetch config file: ' + response.data, `rgba(255,0,0,0.5)`);
                        console.log('Failed to fetch config file: ' + response.data);
                        setIsConfigUnreadable(true);
                    }
                } else {
                    setConfigFileName('');
                    loadLayerSettings(null);
                }

                if (result.tractsS3Id) {
                    await get(`/v1.0/s3Bucket/by-id/${result.tractsS3Id}`);
                    if (isStale()) {
                        return;
                    }
                    if (response.ok) {
                        const contentDisposition = response.headers.get('Content-Disposition');
                        let downloadFileName = '';

                        if (contentDisposition) {
                            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                            if (fileNameMatch && fileNameMatch[1]) {
                                downloadFileName = fileNameMatch[1];
                            }
                        }
                        setTractsFileName(downloadFileName);
                        setCanNavigateToMapping(true);
                    } else {
                        showToast('Failed to fetch tracts file: ' + response.data, `rgba(255,0,0,0.5)`);
                        console.log('Failed to fetch tracts file: ' + response.data);
                        setCanNavigateToMapping(false);
                    }
                } else {
                    setTractsFileName('');
                    setCanNavigateToMapping(false);
                }

                if (result.aoiS3Id) {
                    await get(`/v1.0/s3Bucket/by-id/${result.aoiS3Id}`);
                    if (isStale()) {
                        return;
                    }
                    if (response.ok) {
                        const contentDisposition = response.headers.get('Content-Disposition');
                        let downloadFileName = '';

                        if (contentDisposition) {
                            const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                            if (fileNameMatch && fileNameMatch[1]) {
                                downloadFileName = fileNameMatch[1];
                            }
                        }
                        setAoiFileName(downloadFileName);
                    } else {
                        showToast('Failed to fetch aoi file: ' + response.data, `rgba(255,0,0,0.5)`);
                        console.log('Failed to fetch aoi file: ' + response.data);
                    }
                } else {
                    setAoiFileName('');
                }

                setProjectMapExists(true);
                setMapData(result);
            } else {
                setProjectMapExists(false);
                setMapData(undefined);
            }
            setMapData(result);
            showToast('Map data loaded successfully');
            setIsLoading(false);
        } else {
            console.log('bad request', response.data);
            showToast('Unknown failure: ' + response.data, `rgba(255,0,0,0.5)`);
            setProjectMapExists(false);
            setMapData(undefined);
            // Nothing is known about this project's config, so don't let the checkboxes write one.
            setIsConfigUnreadable(true);
            setIsLoading(false);
        }
    };

    const handleFileUploaded = (formData?: FormData) => {
        const type = formData.get('Type');
        const file = formData.get('File');
        const fileName = formData.get('File Name');

        if (file instanceof Blob && typeof fileName === 'string' && typeof type === 'string') {
            form.delete(type);
            form.append(type, file, fileName);
        }

        showToast('Attached ' + type.toString().toLowerCase() + ' file successfully');

        // form.forEach((value, key) => {
        //     console.log(`${key}:`, value);
        // });
    };

    const downloadFile = async (context?: string, index?: number, fileName?: string) => {
        let s3FileId = 0;
        switch (context) {
            case 'tracts':
                s3FileId = mapData.tractsS3Id;
                break;
            case 'aoi':
                s3FileId = mapData.aoiS3Id;
                break;
            case 'config':
                s3FileId = mapData.configS3Id;
                break;
            default:
                console.log('unknown file context: ', context);
                showToast('Unknown file context: ' + context, `rgba(255,0,0,0.5)`);
                break;
        }

        await get(`/v1.0/s3Bucket/by-id/${s3FileId}`);

        if (response.ok) {
            await response.blob().then((blob) => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.getElementById(`a_${index}`) as HTMLAnchorElement;
                if (a) {
                    a.href = blobUrl;
                    a.download = fileName;
                    a.click();
                    window.URL.revokeObjectURL(blobUrl);
                }
                showToast('Download successful!');
            });
        } else {
            console.log('Failed to fetch file:', response.data);
            showToast('Failed to fetch file: ' + response.data, `rgba(255,0,0,0.5)`);
        }
    };

    const deleteFile = async (context: string) => {
        let s3FileId = 0;
        switch (context) {
            case 'tracts':
                s3FileId = mapData.tractsS3Id;
                mapData.tractsS3Id = null;
                break;
            case 'aoi':
                s3FileId = mapData.aoiS3Id;
                mapData.aoiS3Id = null;
                break;
            case 'config':
                s3FileId = mapData.configS3Id;
                mapData.configS3Id = null;
                break;
            default:
                showToast('Unknown file context: ' + context, `rgba(255,0,0,0.5)`);
                return;
        }

        const selection = selectionRef.current;
        await put(`/v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/map-file/${s3FileId}/delete-project-map-file`, mapData);
        if (selection !== selectionRef.current) {
            return;
        }

        if (response.ok) {
            showToast(`Removed ${context} file successfully`);
            switch (context) {
                case 'tracts':
                    setTractsFileName('');
                    break;
                case 'aoi':
                    setAoiFileName('');
                    break;
                case 'config':
                    setConfigFileName('');
                    break;
                default:
                    break;
            }
        } else {
            showToast(`Unable to ${context} tracts file`, `rgba(255,0,0,0.5)`);
        }
    };

    const onSubmit = async (data: GISMapData) => {
        // The save goes through for the project it was made on. If someone has picked another since,
        // nothing on screen is that project's any more, so what comes back changes nothing.
        const selection = selectionRef.current;
        const isStale = () => selection !== selectionRef.current;
        setIsLoading(true);
        showToast('Submitting data ', `rgba(78,117,154,0.5)`, true);

        // Map Layers: write the checkboxes into the project's config.json, uploaded with this save.
        // A config file attached in this same save is the one updated; otherwise the loaded one.
        let savedConfig: IMapConfig | null = null;
        if (layersChanged) {
            const attached = form.get('Config');
            let base = mapConfig;
            let configName = configFileName || 'config.json';
            if (attached instanceof File) {
                base = parseMapConfig(await attached.text());
                if (isStale()) {
                    return;
                }
                configName = attached.name;
                if (!base) {
                    showToast('The attached config file is not valid JSON, so nothing was saved', `rgba(255,0,0,0.5)`);
                    setIsLoading(false);
                    return;
                }
            }
            savedConfig = withTractLayersEnabled(base, layerEnabled);
            form.delete('Config');
            form.append('Config', new Blob([JSON.stringify(savedConfig, null, 2)], { type: 'application/json' }), configName);
        }

        if (projectMapExists) {
            form.append('MapId', data.mapId);
            form.append('ApiKey', data.apiKey);

            if (mapData.configS3Id) {
                form.append('ConfigS3Id', mapData.configS3Id.toString());
            }

            if (mapData.tractsS3Id) {
                form.append('TractsS3Id', mapData.tractsS3Id.toString());
            }

            if (mapData.aoiS3Id) {
                form.append('AoiS3Id', mapData.aoiS3Id.toString());
            }

            await put(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap`, form);
            if (isStale()) {
                return;
            }

            if (response.ok) {
                setForm(new FormData());
                setCanNavigateToMapping(true);
                if (savedConfig) {
                    loadLayerSettings(savedConfig);
                }
                // An uploaded file gets a NEW S3 id. Pull the map data again so the next save sends
                // the new ids, not the superseded ones (which would repoint the project at old files).
                const updated = await get(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap`);
                if (isStale()) {
                    return;
                }
                setMapData(updated);
                showToast('Update successful');
            } else {
                console.log('bad request', response.data);
                showToast('Update failed: ' + response.data, `rgba(255,0,0,0.5)`);
            }
        } else {
            form.append('MapId', data.mapId);
            form.append('ApiKey', data.apiKey);

            if (!form.get('Tracts')) {
                form.append('Tracts', null);
            }

            if (!form.get('Config')) {
                form.append('Config', null);
            }

            if (!form.get('Aoi')) {
                form.append('Aoi', null);
            }

            await post(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap`, form);
            if (isStale()) {
                return;
            }

            if (response.ok) {
                setForm(new FormData());
                setProjectMapExists(true);
                if (savedConfig) {
                    loadLayerSettings(savedConfig);
                }
                showToast('Upload successful!');
                setCanNavigateToMapping(true);
                const created = await get(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap`);
                if (isStale()) {
                    return;
                }
                setMapData(created);
            } else {
                console.log('bad request', response.data);
                showToast('Upload failed: ' + response.data, `rgba(255,0,0,0.5)`);
            }
        }
        setIsLoading(false);
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const navigateToMapPage = () => {
        if (canNavigateToMapping) {
            const path = `/clients/${selectedClient?.clientNumber}/projects/${selectedProject?.projectNumber}/mapping`;
            window.open(path, '_blank');
        } else {
            showToast('Please ensure a valid tract file is uploaded to view the map', `rgba(255,0,0,0.5)`);
        }
    };

    /*
     * Re-run enrichment over the tracts file already on this project.
     *
     * The map's Title_Status, WI owner / open-acreage composition and per-tract acreage are
     * baked into the GeoJSON when it is uploaded, so they are a snapshot. Leases coming in
     * hand, ownership corrections and changes to the enrichment rules only show up on the map
     * after the file is enriched again — this is that, without making anyone re-upload.
     */
    const reEnrichTractsFile = async () => {
        // FormButton re-enables itself 500ms after a click, so the disabled prop alone can't
        // stop a second click mid-run. Enrichment rewrites the project's tracts file, so it
        // must not overlap with itself.
        if (isLoading) {
            return;
        }

        if (!selectedClient || !selectedProject) {
            showToast('Select a client and project first', `rgba(255,0,0,0.5)`);
            return;
        }

        if (!canNavigateToMapping) {
            showToast('Upload a tracts file before re-enriching', `rgba(255,0,0,0.5)`);
            return;
        }

        setIsLoading(true);
        // Roughly three queries per tract-horizon, so a big project is not instant.
        showToast('Re-enriching tracts file — this can take a few minutes on a large project ', `rgba(78,117,154,0.5)`, true);

        await post(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap/re-enrich`);

        if (response.ok) {
            // Enrichment writes a NEW S3 file and repoints the project at it, so pull the map
            // data again — otherwise the page still holds the superseded tractsS3Id and a
            // download or delete from here would act on the old file.
            setMapData(await get(`v1.0/gis/clients/${selectedClient.id}/projects/${selectedProject.id}/projectMap`));
            showToast('Re-enrich complete — reload the mapping page to see the refreshed data');
        } else {
            console.log('re-enrich failed', response.data);
            showToast('Re-enrich failed: ' + response.data, `rgba(255,0,0,0.5)`);
        }

        setIsLoading(false);
    };

    return (
        <div style={{ overflowY: 'auto' }}>
            <GISNavbar />
            <div>
                {toast && <GISToast message={toast.message} color={toast.color} onClose={() => setToast(null)} persistent={toast.persistent} />}
            </div>

            <div style={{ margin: '2rem' }}>
                <h2 style={{ textAlign: 'center' }}>GIS Portal</h2>
            </div>
            <Page>
                <Page.Section width={6}>
                    <Panel panelTitle="Select Client and Project" fitPanelToScreen={true}>
                        <div style={{ margin: '2rem' }}>
                            <div>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <h3>Clients</h3>
                                        <Filter onFilterChanged={handleClientFilterChanged} showFilterIcon={false} />
                                        <CardContainer
                                            className="clients-page-client-list"
                                            style={{ overflowY: 'auto', maxHeight: '30vh' }}
                                            showNextPagePanel={isOnLastPage}
                                            nextPageAction={props.incrementPage}
                                            buttonPaging={true}
                                        >
                                            {displayExistingClients()}
                                            <div style={{ height: '3rem' }} />
                                        </CardContainer>
                                        <br />
                                        <h3>{selectedClient ? 'Projects for ' + selectedClient.cloakName : 'Projects'}</h3>
                                        <CardContainer
                                            className="projects-page-project-list"
                                            style={{ overflowY: 'auto', maxHeight: '20vh' }}
                                            buttonPaging={true}
                                        >
                                            {displayClientProjects()}
                                            <div style={{ height: '3rem' }} />
                                        </CardContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Panel>
                </Page.Section>
                <Page.Section width={6}>
                    <Panel
                        panelTitle={`${projectMapExists ? 'Update' : 'Upload'} GIS Map Data${selectedProject ? ` for ${selectedProject.name}` : ''}`}
                        fitPanelToScreen={true}
                    >
                        <div style={{ margin: '2rem', position: 'relative' }}>
                            <div className="form-row">
                                <h3 style={{ marginTop: 0 }}>
                                    {projectMapExists ? 'Update' : 'Upload'} GIS Map Data {selectedProject ? ` for ${selectedProject.name}` : ''}
                                </h3>
                                <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                    <Button text="Go to Mapping Page" onClick={navigateToMapPage} style={{ width: 'auto' }} />
                                    <Button
                                        text="Re-Enrich Tracts File"
                                        title="Re-run enrichment over the uploaded tracts file, refreshing title status, WI owners and acreage from the database"
                                        onClick={reEnrichTractsFile}
                                        disabled={isLoading || !canNavigateToMapping}
                                        style={{ width: 'auto', marginTop: '0.5rem' }}
                                    />
                                </div>
                            </div>
                            <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <GISPortalFormDetails
                                        register={register}
                                        control={control}
                                        errors={errors}
                                        setValue={setValue}
                                        projectId={selectedProject ? selectedProject.id : null}
                                        onFileUploaded={handleFileUploaded}
                                        apiKey={selectedProject && mapData ? mapData.apiKey : null}
                                        mapId={selectedProject && mapData ? mapData.mapId : null}
                                        isUpdate={mapData ? true : false}
                                        onDownload={downloadFile}
                                        onDelete={deleteFile}
                                        configFileName={configFileName}
                                        tractsFileName={tractsFileName}
                                        aoiFileName={aoiFileName}
                                        showToast={showToast}
                                    />
                                    <GISPortalLayerSettings
                                        enabled={layerEnabled}
                                        onChange={setLayer}
                                        disabled={!selectedProject || isConfigUnreadable}
                                        note={
                                            isConfigUnreadable
                                                ? 'The config file for this project could not be read, so its layers cannot be changed here.'
                                                : undefined
                                        }
                                    />
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '5px', justifyContent: 'flex-end' }}>
                                        <Button text="Save" type="submit" intent="primary" />
                                    </div>
                                </div>
                            </Form>
                            {isLoading && (
                                <div
                                    style={{
                                        top: 0,
                                        left: 0,
                                        position: 'absolute',
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 9999,
                                    }}
                                >
                                    <LoadingSpinner />
                                </div>
                            )}
                        </div>
                    </Panel>
                </Page.Section>
            </Page>
        </div>
    );
};

const mapStateToProps = (state: IRootState): IGISPortalStoreProps => {
    return {
        clients: selectors.client.clients(state),
    };
};

const mapDispatchToProps: IGISPortalDispatchProps = {
    getClients: (options: ODataOptions, resetSearch: boolean) => actions.client.getClients(options, resetSearch),
    initializePage: () => actions.client.initializeClientPage(),
    incrementPage: () => actions.client.incrementClientPage(),
    decrementPage: () => actions.client.decrementClientPage(),
};

export default connect(mapStateToProps, mapDispatchToProps)(GISPortalPage);



// WEBPACK FOOTER //
// ./src/components/gis/portal/GISPortal.tsx